import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "./Icon";
import { haptic } from "../telegram/haptics";
import { ui } from "../motion";
import s from "./Toast.module.css";

interface Toast {
  id: number;
  text: string;
  tone: "info" | "error";
  action?: { label: string; run: () => void };
}

interface ToastApi {
  show(text: string, options?: { tone?: Toast["tone"]; action?: Toast["action"] }): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const LIFETIME = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback<ToastApi["show"]>(
    (text, options) => {
      const id = nextId.current++;
      const tone = options?.tone ?? "info";
      if (tone === "error") haptic.error();
      setToasts((current) => [...current.slice(-2), { id, text, tone, action: options?.action }]);
      window.setTimeout(() => dismiss(id), LIFETIME);
    },
    [dismiss],
  );

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={s.wrap} role="status" aria-live="polite">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              className={[s.toast, "chrome", toast.tone === "error" && s.error]
                .filter(Boolean)
                .join(" ")}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={ui}
              layout
            >
              {toast.tone === "error" && <Icon name="alert" size={20} />}
              <span className={s.text}>{toast.text}</span>
              {toast.action && (
                <button
                  type="button"
                  className={s.action}
                  onClick={() => {
                    haptic.tap();
                    toast.action!.run();
                    dismiss(toast.id);
                  }}
                >
                  <Icon name="undo" size={16} />
                  {toast.action.label}
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside ToastProvider");
  return api;
}
